import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Check, Clock, FileDown, Gavel, Loader2, Plus, Store, X, Zap } from "lucide-react";
import { useApp } from "../context/AppContext";
import { authHeaders, errCode } from "../context/AuthContext";
import { SAR, useNow, remainingLabel } from "../souq";
import { Skel } from "./Skeleton";
import LuxDialog from "./ui/LuxDialog";
import BuyerAccessCard from "./BuyerAccessCard";
import { VerifiedTraderBadge } from "./SouqLive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TRADER_VARIETIES = [
  ["Sukkari", "سكري"], ["Ajwa", "عجوة"], ["Khalas", "خلاص"], ["Safawi", "صفاوي"], ["Barhi", "برحي"], ["Khudri", "خضري"], ["Segai", "صقعي"], ["Anbara", "عنبرة"],
  ["Mabroom", "مبروم"], ["Barni", "برني"], ["Rothana", "روثانة"], ["Shishi", "شيشي"], ["Ruzeiz", "رزيز"], ["Sullaj", "صلج"], ["HelwatAlJouf", "حلوة الجوف"], ["Bayadh", "بياض"],
].map(([id, ar]) => ({ id, en: id === "HelwatAlJouf" ? "Helwat Al-Jouf" : id, ar }));
export const TRADER_REGIONS = [{ en: "Al-Ahsa", ar: "الأحساء" }, { en: "Al-Qassim", ar: "القصيم" }, { en: "Al-Madinah", ar: "المدينة المنورة" }, { en: "Riyadh", ar: "الرياض" }];

async function downloadInvoice(o, failMsg) {
  try {
    const res = await fetch(`${API}/souq/orders/${o.order_id}/invoice.pdf`, { headers: authHeaders() });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `settlement-${o.order_id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch { toast.error(failMsg); }
}
const ST_CHIP = { active: "chip-ok", "ended-pending": "chip-warn", sold: "chip-ok", "expired-unsold": "chip-bad", "closed-declined": "chip-bad", cancelled: "chip-bad", removed: "chip-bad" };

export default function FarmerSouqPanel({ yields, mode = "farmer" }) {
  const isTrader = mode === "trader";
  const { t, lang } = useApp();
  const now = useNow(15000);
  const [listings, setListings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState(null);
  const [form, setForm] = useState({ yield_id: "", variety: "Sukkari", quantity_kg: "", region: "Al-Qassim", starting_price: "", buy_now_price: "", duration_days: 7, desc_ar: "", desc_en: "" });

  const load = useCallback(() => {
    axios.get(`${API}/souq/my/listings`, { headers: authHeaders() }).then((r) => setListings(r.data)).catch(() => setListings([]));
    axios.get(`${API}/souq/my/orders`, { headers: authHeaders() }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeYieldIds = new Set((listings || []).filter((l) => ["active", "ended-pending"].includes(l.status)).map((l) => l.yield_id));
  const available = (yields || []).filter((y) => !activeYieldIds.has(y.yield_id));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const common = { starting_price: Number(form.starting_price), buy_now_price: form.buy_now_price ? Number(form.buy_now_price) : null,
        duration_days: Number(form.duration_days), desc_ar: form.desc_ar || null, desc_en: form.desc_en || null };
      const url = isTrader ? `${API}/souq/listings/manual` : `${API}/souq/listings`;
      const body = isTrader ? { ...common, variety: form.variety, quantity_kg: Number(form.quantity_kg), region: form.region } : { ...common, yield_id: form.yield_id };
      await axios.post(url, body, { headers: authHeaders() });
      toast.success(t("fs.created"));
      setCreating(false);
      setForm((f) => ({ ...f, yield_id: "", quantity_kg: "", starting_price: "", buy_now_price: "", duration_days: 7, desc_ar: "", desc_en: "" }));
      load();
    } catch (err) { toast.error(t(`err.${errCode(err)}`)); }
    finally { setBusy(false); }
  };

  const act = async (id, action, okMsg) => {
    try {
      await axios.post(`${API}/souq/listings/${id}/${action}`, {}, { headers: authHeaders() });
      toast.success(okMsg);
      load();
    } catch (err) { toast.error(t(`err.${errCode(err)}`)); load(); }
  };

  return (
    <section className="dash-panel" data-testid={isTrader ? "trader-souq-panel" : "farmer-souq-panel"}>
      {!isTrader && <BuyerAccessCard />}
      <div className="wq-profile-head">
        <h2 className="dash-panel-title">{isTrader ? t("tr.title") : t("fs.title")}</h2>
        <button className="btn-lux-gold" onClick={() => setCreating((c) => !c)} data-testid="new-listing-button">
          {creating ? <X size={14} /> : <Plus size={14} />} {creating ? t("souq.cancelBtn") : t("fs.new")}
        </button>
      </div>

      {creating && (
        <form className="fs-form glass-card" onSubmit={create} data-testid="new-listing-form">
          {isTrader ? (
            <>
              <p className="po-hint" data-testid="trader-listing-note">{t("tr.note")}</p>
              <div className="fs-form-row">
                <div className="p3-field"><label className="p3-label">{t("an.variety")}</label>
                  <select className="p3-input" required value={form.variety} onChange={(e) => setForm({ ...form, variety: e.target.value })} data-testid="listing-variety-select">
                    {TRADER_VARIETIES.map((v) => <option key={v.id} value={v.id}>{lang === "ar" ? v.ar : v.en}</option>)}
                  </select></div>
                <div className="p3-field"><label className="p3-label">{t("tr.quantity")}</label>
                  <input type="number" min={1} step={1} required className="p3-input" dir="ltr" value={form.quantity_kg} onChange={(e) => setForm({ ...form, quantity_kg: e.target.value })} data-testid="listing-quantity-input" /></div>
                <div className="p3-field"><label className="p3-label">{t("tr.origin")}</label>
                  <select className="p3-input" required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="listing-region-select">
                    {TRADER_REGIONS.map((r) => <option key={r.en} value={r.en}>{lang === "ar" ? r.ar : r.en}</option>)}
                  </select></div>
              </div>
            </>
          ) : (
          <div className="p3-field">
            <label className="p3-label">{t("fs.selectYield")}</label>
            <select className="p3-input" required value={form.yield_id} onChange={(e) => setForm({ ...form, yield_id: e.target.value })} data-testid="listing-yield-select">
              <option value="">—</option>
              {available.map((y) => (
                <option key={y.yield_id} value={y.yield_id}>
                  {(lang === "ar" ? y.variety_ar : y.variety_en)} · {y.quantity_kg} kg · {y.harvest_date}{y.cert_id ? " ✓" : ""}
                </option>
              ))}
            </select>
            {available.length === 0 && <small className="admin-muted">{t("fs.noneAvailable")}</small>}
          </div>
          )}
          <div className="fs-form-row">
            <div className="p3-field"><label className="p3-label">{t("fs.start")} (SAR)</label>
              <input type="number" min={1} step={1} required className="p3-input" dir="ltr" value={form.starting_price} onChange={(e) => setForm({ ...form, starting_price: e.target.value })} data-testid="listing-start-input" /></div>
            <div className="p3-field"><label className="p3-label">{t("fs.buynow")}</label>
              <input type="number" min={1} step={1} className="p3-input" dir="ltr" value={form.buy_now_price} onChange={(e) => setForm({ ...form, buy_now_price: e.target.value })} data-testid="listing-buynow-input" /></div>
            <div className="p3-field"><label className="p3-label">{t("fs.duration")}</label>
              <select className="p3-input" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="listing-duration-select">
                {[3, 7, 14].map((d) => <option key={d} value={d}>{d} {t("fs.days")}</option>)}
              </select></div>
          </div>
          <div className="fs-form-row">
            <div className="p3-field"><label className="p3-label">{t("fs.descAr")}</label>
              <input className="p3-input" dir="rtl" value={form.desc_ar} onChange={(e) => setForm({ ...form, desc_ar: e.target.value })} data-testid="listing-descar-input" /></div>
            <div className="p3-field"><label className="p3-label">{t("fs.descEn")}</label>
              <input className="p3-input" dir="ltr" value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} data-testid="listing-descen-input" /></div>
          </div>
          <button type="submit" className="btn-lux-gold" disabled={busy || (!isTrader && !form.yield_id)} data-testid="create-listing-submit">
            {busy ? <Loader2 size={15} className="spin" /> : <Gavel size={15} />} {t("fs.create")}
          </button>
        </form>
      )}

      {listings === null ? <Skel h={90} /> : listings.length === 0 ? <p className="dash-empty" data-testid="fs-empty">{t("fs.noListings")}</p> : (
        <div className="souq-rows">
          {listings.map((l) => (
            <div key={l.listing_id} className="souq-row glass-card" data-testid={`fs-listing-${l.listing_id}`}>
              <div className="souq-row-main">
                <strong>{lang === "ar" ? l.variety_ar : l.variety_en} · {l.quantity_kg} kg
                  {l.verified && <em className="souq-badge"><BadgeCheck size={11} /> {t("souq.verified")}</em>}
                  {l.seller_type === "trader" && (l.seller_verified ? <VerifiedTraderBadge /> : <em className="souq-trader-chip"><Store size={11} /> {t("souq.traderListing")}</em>)}</strong>
                <small>
                  <Link to={`/souq/listing/${l.listing_id}`} className="gold-link">{l.listing_id}</Link>
                  {" · "}{l.bid_count === 1 ? t("souq.bid1") : `${l.bid_count} ${t("souq.bids")}`}
                  {l.buy_now_price ? <> · <Zap size={10} /> {SAR(l.buy_now_price, lang)}</> : null}
                </small>
              </div>
              <div className="souq-row-nums">
                <span>{t("souq.currentBid")}: <strong dir="ltr">{SAR(l.current_price, lang)}</strong></span>
                {l.status === "active" && <span className="souq-countdown"><Clock size={12} /> {remainingLabel(l.ends_at, now, t)}</span>}
                {l.winner_name && l.status !== "active" && <span>{t("fs.winner")}: {l.winner_name}</span>}
              </div>
              <div className="fs-actions">
                <span className={`admin-chip ${ST_CHIP[l.status] || ""}`} data-testid={`fs-status-${l.listing_id}`}>{t(`souq.status.${l.status}`)}</span>
                {l.status === "active" && l.bid_count === 0 && (
                  <button className="admin-btn admin-btn-warn" onClick={() => act(l.listing_id, "cancel", t("fs.cancelled"))} data-testid={`fs-cancel-${l.listing_id}`}><X size={13} /> {t("fs.cancel")}</button>
                )}
                {l.status === "ended-pending" && (
                  <>
                    <button className="admin-btn admin-btn-ok" onClick={() => setAccepting(l)} data-testid={`fs-accept-${l.listing_id}`}><Check size={13} /> {t("fs.accept")}</button>
                    <button className="admin-btn admin-btn-warn" onClick={() => act(l.listing_id, "decline", t("fs.declined"))} data-testid={`fs-decline-${l.listing_id}`}><X size={13} /> {t("fs.decline")}</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {orders.length > 0 && (
        <>
          <h3 className="dash-panel-title fs-orders-title">{t("fs.orders")}</h3>
          <div className="souq-rows" data-testid="fs-orders">
            {orders.map((o) => (
              <div key={o.order_id} className="souq-row glass-card" data-testid={`fs-order-${o.order_id}`}>
                <div className="souq-row-main">
                  <strong>{lang === "ar" ? o.variety_ar : o.variety_en} · {SAR(o.amount, lang)}</strong>
                  <small><code dir="ltr">{o.order_id}</code> · {o.buyer_name}</small>
                </div>
                <div className="souq-row-nums fs-fees" data-testid={`fs-order-fees-${o.order_id}`}>
                  <span>{t("fs.commission")}: <strong dir="ltr">-{SAR(o.fees.commission, lang)}</strong></span>
                  <span>{t("fs.vat")}: <strong dir="ltr">-{SAR(o.fees.vat, lang)}</strong></span>
                  <span className="fs-net">{t("fs.net")}: <strong dir="ltr">{SAR(o.fees.net_to_farmer, lang)}</strong></span>
                </div>
                <div className="pay-actions">
                  {o.status === "paid" && (
                    <button className="btn-lux-ghost" onClick={() => downloadInvoice(o, t("pay.dlFail"))} data-testid={`fs-invoice-dl-${o.order_id}`}>
                      <FileDown size={14} /> {t("pay.invoice")}
                    </button>
                  )}
                  <span className={`admin-chip ${o.status === "paid" ? "chip-ok" : "chip-warn"}`}>{t(`order.status.${o.status}`)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <LuxDialog
        open={accepting !== null} onOpenChange={(o) => !o && setAccepting(null)}
        title={t("fs.acceptTitle")}
        description={accepting ? `${t("fs.acceptDesc")} — ${SAR(accepting.current_price, lang)} (${accepting.winner_name || ""})` : ""}
        confirmLabel={t("fs.accept")} cancelLabel={t("souq.cancelBtn")} testId="accept-dialog"
        onConfirm={() => { const id = accepting.listing_id; setAccepting(null); act(id, "accept", t("fs.accepted")); }}
      />
    </section>
  );
}
